import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, effect, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { RouterModule } from '@angular/router';
import { ChatComponent, SplitPaneComponent } from '@components';
import { SplitPaneVerticalComponent, HistoryComponent } from '@components';
import { DevopsFacade, ReposFacade, WorkspaceFacade } from '@state';

@Component({
  selector: 'app-devops',
  templateUrl: './devops.page.html',
  styleUrls: ['./devops.page.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ChatComponent,
    HistoryComponent,
    SplitPaneComponent,
    SplitPaneVerticalComponent
],
})
export class DevopsPage implements OnInit, OnDestroy {
  public readonly devopsStore = inject(DevopsFacade);
  public readonly reposStore = inject(ReposFacade);
  private readonly rootStore = inject(WorkspaceFacade);
  private projectLoadVersion = 0;

  public loginEmail = '';
  public loginPassword = '';
  public authenticatorCode = '';

  public readonly activeProjectId = computed(() => this.rootStore.activeProjectId());
  public readonly sessionDetermined = computed(() => this.devopsStore.platform() !== null);

  public devopsSuggestions: any[] = [
    { icon: 'bi bi-person-check', prompt: 'Check my Code Capsules platform session' },
    { icon: 'bi bi-diagram-3', prompt: 'Show the Code Capsules team matched to this project' },
    { icon: 'bi bi-cloud-arrow-up', prompt: 'What spaces and capsules are available for this project?' },
  ];

  constructor() {
    effect(() => {
      const projectId = this.activeProjectId();
      const loadVersion = ++this.projectLoadVersion;
      if (!projectId) {
        this.devopsStore.reset();
        return;
      }
      void this.loadProjectContext(projectId, loadVersion);
    });

    effect(() => {
      const spaces = this.devopsStore.spaces();
      const selectedSpaceId = this.devopsStore.selectedSpaceId();
      if (selectedSpaceId || !spaces.length) return;
      const firstSpaceId = String(spaces[0]?.id ?? '').trim();
      if (!firstSpaceId) return;
      void this.devopsStore.selectSpace(firstSpaceId);
    });
  }

  get devopsContext(): any {
    return {
      page: 'devops',
      purpose: 'Code Capsules team, space, and capsule visibility for the selected project',
      capabilities: this.devopsStore.capabilities(),
      identity: this.devopsStore.identity(),
      platform: this.devopsStore.platform(),
      selectedTeam: this.devopsStore.selectedTeam(),
      spaces: this.devopsStore.spaces(),
      spaceGroups: this.devopsStore.spaceGroups(),
      capsules: this.devopsStore.capsules(),
      deployments: this.devopsStore.webDeployments(),
      incidents: this.devopsStore.incidents(),
      buildLog: this.devopsStore.buildLog(),
      outputs: ['Auth status', 'Matched team summary', 'Project deployment visibility'],
    };
  }

  get orchestratorPageContext(): any {
    return {
      capabilities: this.devopsStore.capabilities(),
      identity: this.devopsStore.identity(),
      platform: this.devopsStore.platform(),
      selectedTeam: this.devopsStore.selectedTeam(),
      spaces: this.devopsStore.spaces(),
      spaceGroups: this.devopsStore.spaceGroups(),
      capsules: this.devopsStore.capsules(),
      webDeployments: this.devopsStore.webDeployments(),
      builds: this.devopsStore.builds(),
      buildLog: this.devopsStore.buildLog(),
      incidents: this.devopsStore.incidents(),
      notes: this.devopsStore.notes(),
    };
  }

  get selectedTeam(): any | null {
    return this.devopsStore.selectedTeam();
  }

  get selectedSpace(): any | null {
    const selectedSpaceId = this.devopsStore.selectedSpaceId();
    return this.devopsStore.spaces().find((space) => space.id === selectedSpaceId) ?? null;
  }

  get selectedCapsule(): any | null {
    const selectedCapsuleId = this.devopsStore.selectedCapsuleId();
    if (!selectedCapsuleId) return null;
    return this.devopsStore.capsules().find((capsule) => capsule.id === selectedCapsuleId) ?? null;
  }

  get teamSpaces(): any[] {
    return this.devopsStore.spaces();
  }

  isHealthyStatus(status: string | null | undefined): boolean {
    const normalized = String(status ?? '').toLowerCase();
    return (
      normalized === 'running' ||
      normalized === 'healthy' ||
      normalized === 'active' ||
      normalized === 'deployed' ||
      normalized === 'ready'
    );
  }

  async closeCapsuleDetails(): Promise<void> {
    await this.devopsStore.selectCapsule('');
  }

  async ngOnInit(): Promise<void> {}

  ngOnDestroy(): void {}

  async refresh(): Promise<void> {
    await this.reposStore.load();
    await this.devopsStore.loadOverview();
  }

  async login(): Promise<void> {
    await this.devopsStore.login(this.loginEmail.trim(), this.loginPassword);
    if (!this.devopsStore.platform()?.pendingTwoFactor) {
      this.loginPassword = '';
    }
  }

  async verifyTwoFactor(): Promise<void> {
    await this.devopsStore.verifyTwoFactor(this.authenticatorCode.trim());
    if (this.devopsStore.platform()?.authenticated) {
      this.authenticatorCode = '';
      this.loginPassword = '';
    }
  }

  async logout(): Promise<void> {
    await this.devopsStore.logout();
  }

  async onSpaceChange(spaceId: string): Promise<void> {
    await this.devopsStore.selectSpace(spaceId);
  }

  async onCapsuleChange(capsuleId: string): Promise<void> {
    await this.devopsStore.selectCapsule(capsuleId);
  }

  async onBuildChange(buildId: string): Promise<void> {
    await this.devopsStore.selectBuild(buildId);
  }

  private async loadProjectContext(projectId: string, loadVersion: number): Promise<void> {
    await this.reposStore.load();
    if (loadVersion !== this.projectLoadVersion || this.activeProjectId() !== projectId) return;
    await this.devopsStore.loadOverview({
      spaceId: null,
      capsuleId: null,
      buildId: null,
    });
  }
}